import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { getUserById } from './userService';

/**
 * Create a notification for an artist
 * @param {string} userId - The artist's UID
 * @param {string} type - Notification type: "follow" | "tip" | "mention"
 * @param {string} message - The notification message
 * @param {string} senderId - Optional: The sender's UID (for navigation)
 */
export async function createNotification(userId, type, message, senderId = null) {
    try {
        await addDoc(collection(db, 'notifications'), {
            userId,
            type,
            message,
            senderId: senderId || null,
            createdAt: serverTimestamp(),
            read: false
        });
        // Increment user's unreadNotifications counter for lightweight badges
        try {
            await updateDoc(doc(db, 'users', userId), { unreadNotifications: increment(1) });
        } catch (e) {
            // User doc might not exist or permission denied; ignore counter update
        }
    } catch (error) {
        console.error('Error creating notification:', error);
        throw error;
    }
}

/**
 * Create a follow notification when someone follows an artist or listener
 * @param {string} followerUid - The person who followed
 * @param {string} targetUid - The person who was followed (artist or listener)
 */
export async function createFollowNotification(followerUid, targetUid) {
    try {
        // Get follower's name
        const follower = await getUserById(followerUid);
        const followerName = follower?.name || follower?.displayName || 'Someone';
        
        // Get target user to check their role
        const target = await getUserById(targetUid);
        if (!target) {
            console.warn('Target user not found for follow notification:', targetUid);
            return;
        }

        // Create notification for all users (artist or listener) when someone follows them
        const message = `@${followerName} started following you!`;
        await createNotification(targetUid, 'follow', message, followerUid);
    } catch (error) {
        console.error('Error creating follow notification:', error);
    }
}

/**
 * Create a tip notification when an artist receives a tip
 * @param {string} senderUid - The person who sent the tip
 * @param {string} artistUid - The artist who received the tip
 * @param {number} amount - The tip amount
 * @param {string} tipMessage - Optional message sent with the tip
 */
export async function createTipNotification(senderUid, artistUid, amount, tipMessage = '') {
    try {
        // Get sender's name
        const sender = await getUserById(senderUid);
        const senderName = sender?.name || sender?.displayName || 'Listener';
        
        // Get artist to check if they're an artist
        const artist = await getUserById(artistUid);
        if (artist?.role !== 'artist') {
            // Only create notification for artists
            return;
        }

        let message = `🎉 You received a ₹${amount} tip from @${senderName}!`;
        if (tipMessage && tipMessage.trim()) {
            message += ` Message: "${tipMessage.trim()}"`;
        }
        await createNotification(artistUid, 'tip_confirmed', message, senderUid);
    } catch (error) {
        console.error('Error creating tip notification:', error);
    }
}

/**
 * Create notification when listener earns a badge from an artist
 * @param {string} listenerUid - The listener who got the badge
 * @param {string} artistName - The artist's name
 * @param {string} badgeType - bronze | silver | gold | diamond | crown
 * @param {string} artistUid - The artist UID (for navigation)
 */
export async function createBadgeEarnedNotification(listenerUid, artistName, badgeType, artistUid = null) {
    try {
        const badgeLabel = badgeType ? `${badgeType.charAt(0).toUpperCase() + badgeType.slice(1)}` : 'Supporter';
        const message = `🏅 You earned @${artistName}'s ${badgeLabel} Badge!`;
        await createNotification(listenerUid, 'badge_earned', message, artistUid);
    } catch (error) {
        console.error('Error creating badge earned notification:', error);
    }
}

export async function createTipPendingNotification(senderUid, artistUid, amount) {
    try {
        const sender = await getUserById(senderUid);
        const senderName = sender?.name || sender?.displayName || 'Listener';
        const artist = await getUserById(artistUid);
        if (artist?.role !== 'artist') {
            return;
        }
        const message = `⏳ @${senderName} started a ₹${amount} tip. Awaiting admin approval.`;
        await createNotification(artistUid, 'tip_pending', message, senderUid);
    } catch (error) {
        console.error('Error creating pending tip notification:', error);
    }
}

export async function createTipFailedNotification(senderUid, artistUid, amount) {
    try {
        const artist = await getUserById(artistUid);
        const artistName = artist?.name || artist?.displayName || 'the artist';
        const message = `❌ Your ₹${amount} tip to @${artistName} was declined.`;
        await createNotification(senderUid, 'tip_failed', message, artistUid);
    } catch (error) {
        console.error('Error creating failed tip notification:', error);
    }
}

/**
 * Create a message notification when a listener receives a message
 * @param {string} senderUid - The person who sent the message
 * @param {string} receiverUid - The listener who received the message
 * @param {string} messageText - The message text (optional, for preview)
 */
export async function createMessageNotification(senderUid, receiverUid, messageText = '') {
    try {
        // Get sender's name
        const sender = await getUserById(senderUid);
        const senderName = sender?.name || sender?.displayName || 'Someone';
        
        // Get receiver to check if they're a listener
        const receiver = await getUserById(receiverUid);
        if (receiver?.role !== 'listener') {
            // Only create notification for listeners
            return;
        }

        const preview = messageText.length > 50 ? messageText.substring(0, 50) + '...' : messageText;
        const message = `💬 New message from @${senderName}${preview ? `: "${preview}"` : ''}`;
        await createNotification(receiverUid, 'message', message, senderUid);
    } catch (error) {
        console.error('Error creating message notification:', error);
    }
}

/**
 * Create song post notifications for all listeners who follow an artist
 * @param {string} artistUid - The artist who posted the song
 * @param {string} songTitle - The title of the song
 */
export async function createSongPostNotifications(artistUid, songTitle) {
    try {
        // Get artist info
        const artist = await getUserById(artistUid);
        if (!artist || artist.role !== 'artist') {
            // Only create notifications for artists
            return;
        }

        const artistName = artist?.name || artist?.displayName || 'Artist';
        
        // Get artist's user document to get followers list
        const artistRef = doc(db, 'users', artistUid);
        const artistSnap = await getDoc(artistRef);
        
        if (!artistSnap.exists()) {
            console.warn('Artist document not found:', artistUid);
            return;
        }

        // Get followers from subcollection
        const { getFollowersList } = await import('./userService');
        const followers = await getFollowersList(artistUid);
        
        if (followers.length === 0) {
            // No followers, no notifications needed
            return;
        }

        // Create notifications for all followers who are listeners
        const notificationPromises = [];
        const message = `🎵 @${artistName} posted a new song: "${songTitle}"`;

        for (const followerUid of followers) {
            // Check if follower is a listener
            try {
                const follower = await getUserById(followerUid);
                if (follower && follower.role === 'listener') {
                    // Create notification for this listener (include artistId for navigation)
                    notificationPromises.push(
                        createNotification(followerUid, 'song_post', message, artistUid).catch(err => {
                            console.error(`Failed to create notification for follower ${followerUid}:`, err);
                        })
                    );
                }
            } catch (err) {
                console.error(`Error checking follower ${followerUid}:`, err);
            }
        }

        // Wait for all notifications to be created (fire and forget)
        await Promise.allSettled(notificationPromises);
        console.log(`Created song post notifications for ${notificationPromises.length} followers`);
    } catch (error) {
        console.error('Error creating song post notifications:', error);
    }
}

